/**
 * THE FIRESTORE SWITCH (the public demo, exception M1 in CLAUDE.md).
 *
 * Every `firebase/firestore` import in src/ — static or `import()` — resolves to this module, through one line of
 * package.json: `"browser": {"firebase/firestore": "./src/demo/firestoreSwitch.ts"}` (webpack applies the app's own
 * browser field to the app's own files; jest and TypeScript do not, so tests and types keep the real SDK). This module
 * imports the SDK itself as `@firebase/firestore`, the package `firebase/firestore` re-exports, so the alias never
 * reaches it.
 *
 * OFF THE DEMO every export is the real SDK's: the ones below that the demo needs call the real function with the
 * same arguments and return its result, after one boolean test. Nothing else changes for anyone signed in to
 * prest0.ai.
 *
 * ON A DEMO PAGE (src/demo/demoAddress.ts) the real SDK still does the work a page can see — references, queries,
 * listeners, snapshots, cursors, timestamps — on an in-memory cache that never connects (the demo's own project,
 * network disabled before first use). What it cannot do offline is hand back what a server would: those calls go to
 * the demo's engine (src/demo/engine/firestore/), which the demo's runtime installs before the app draws its first
 * frame. A call that arrives earlier waits for it.
 *
 * Sentinels (`serverTimestamp()`, `increment()`, `arrayUnion()`, `arrayRemove()`, `deleteField()`) stay the real
 * SDK's objects, so its validation of a write is unchanged; on a demo page each is also recorded here, so the engine
 * can apply it as the server would.
 */
import {
  addDoc as realAddDoc,
  arrayRemove as realArrayRemove,
  arrayUnion as realArrayUnion,
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator as realConnectFirestoreEmulator,
  deleteDoc as realDeleteDoc,
  deleteField as realDeleteField,
  disableNetwork,
  getDoc as realGetDoc,
  getDocFromCache as realGetDocFromCache,
  getDocFromServer as realGetDocFromServer,
  getDocs as realGetDocs,
  getDocsFromCache as realGetDocsFromCache,
  getDocsFromServer as realGetDocsFromServer,
  getFirestore as realGetFirestore,
  increment as realIncrement,
  initializeFirestore as realInitializeFirestore,
  memoryLocalCache,
  memoryLruGarbageCollector,
  onSnapshot as realOnSnapshot,
  runTransaction as realRunTransaction,
  serverTimestamp as realServerTimestamp,
  setDoc as realSetDoc,
  updateDoc as realUpdateDoc,
  writeBatch as realWriteBatch,
  type DocumentReference,
  type Firestore,
  type FirestoreSettings,
  type Query,
  type Unsubscribe,
} from '@firebase/firestore';
import { getApp, type FirebaseApp } from '@firebase/app';
import { isDemoPage } from './demoAddress';

export * from '@firebase/firestore';

/** What a recorded sentinel asks the engine to do with a field. */
export type DemoSentinel =
  | { kind: 'serverTimestamp' }
  | { kind: 'deleteField' }
  | { kind: 'increment'; operand: number }
  | { kind: 'arrayUnion'; elements: unknown[] }
  | { kind: 'arrayRemove'; elements: unknown[] };

/**
 * The demo engine's side of the switch: the calls a server would answer. Installed once by the demo's runtime
 * (src/demo/engine/firestore/), never off a demo page.
 */
export interface DemoFirestoreEngine {
  getDoc(ref: DocumentReference): Promise<unknown>;
  getDocs(query: Query): Promise<unknown>;
  onSnapshot(target: DocumentReference | Query, rest: unknown[]): Unsubscribe;
  setDoc(ref: DocumentReference, data: unknown, options?: unknown): Promise<void>;
  updateDoc(ref: DocumentReference, rest: unknown[]): Promise<void>;
  addDoc(collection: unknown, data: unknown): Promise<DocumentReference>;
  deleteDoc(ref: DocumentReference): Promise<void>;
  writeBatch(db: Firestore): unknown;
  runTransaction(db: Firestore, update: (transaction: unknown) => Promise<unknown>, options?: unknown): Promise<unknown>;
}

const DEMO = isDemoPage();
const sentinels = new WeakMap<object, DemoSentinel>();
let engine: DemoFirestoreEngine | null = null;
let engineArrived: (() => void) | null = null;
const engineReady: Promise<void> = new Promise((resolve) => {
  engineArrived = resolve;
});

/** The demo runtime hands its engine over here, once, before the app draws. */
export function installDemoFirestoreEngine(next: DemoFirestoreEngine): void {
  if (!DEMO || engine) return;
  engine = next;
  if (engineArrived) engineArrived();
}

/** The sentinel a write's field holds, if this page recorded one (demo pages only). */
export function demoSentinelOf(value: unknown): DemoSentinel | undefined {
  return value !== null && typeof value === 'object' ? sentinels.get(value as object) : undefined;
}

function viaEngine<T>(run: (e: DemoFirestoreEngine) => Promise<T>): Promise<T> {
  return engine ? run(engine) : engineReady.then(() => run(engine as DemoFirestoreEngine));
}

// ── The instance ─────────────────────────────────────────────────────────────

let demoDb: Firestore | null = null;

/**
 * The demo page's one Firestore: the demo project's, in memory, with collection off (every document the engine
 * publishes stays readable) and the network disabled before any use, so it never connects to anything.
 */
function demoFirestore(app?: FirebaseApp, settings?: FirestoreSettings): Firestore {
  if (demoDb) return demoDb;
  const own: FirestoreSettings = {
    ...(settings && settings.ignoreUndefinedProperties ? { ignoreUndefinedProperties: true } : {}),
    localCache: memoryLocalCache({
      garbageCollector: memoryLruGarbageCollector({ cacheSizeBytes: CACHE_SIZE_UNLIMITED }),
    }),
  };
  // Never `getFirestore()` here: on an app with no Firestore yet it would start one with the default settings (eager
  // collection), and this instance's settings could no longer be set.
  demoDb = realInitializeFirestore(app || getApp(), own);
  void disableNetwork(demoDb).catch(() => undefined);
  return demoDb;
}

/** The demo page's Firestore, for the engine (null off a demo page). */
export function demoFirestoreInstance(): Firestore | null {
  return DEMO ? demoFirestore() : null;
}

export function initializeFirestore(
  ...args: Parameters<typeof realInitializeFirestore>
): ReturnType<typeof realInitializeFirestore> {
  if (!DEMO) return realInitializeFirestore(...args);
  return demoFirestore(args[0], args[1]);
}

export function getFirestore(...args: unknown[]): Firestore {
  if (!DEMO) return (realGetFirestore as (...a: unknown[]) => Firestore)(...args);
  const first = args[0];
  const app = first && typeof first === 'object' && 'options' in (first as object) ? (first as FirebaseApp) : undefined;
  return demoFirestore(app);
}

export function connectFirestoreEmulator(
  ...args: Parameters<typeof realConnectFirestoreEmulator>
): ReturnType<typeof realConnectFirestoreEmulator> {
  if (!DEMO) return realConnectFirestoreEmulator(...args);
  return undefined as ReturnType<typeof realConnectFirestoreEmulator>;
}

// ── Reads ────────────────────────────────────────────────────────────────────

export function getDoc(...args: Parameters<typeof realGetDoc>): ReturnType<typeof realGetDoc> {
  if (!DEMO) return realGetDoc(...args);
  return viaEngine((e) => e.getDoc(args[0] as DocumentReference)) as ReturnType<typeof realGetDoc>;
}

export function getDocFromServer(...args: Parameters<typeof realGetDocFromServer>): ReturnType<typeof realGetDocFromServer> {
  if (!DEMO) return realGetDocFromServer(...args);
  return viaEngine((e) => e.getDoc(args[0] as DocumentReference)) as ReturnType<typeof realGetDocFromServer>;
}

export function getDocFromCache(...args: Parameters<typeof realGetDocFromCache>): ReturnType<typeof realGetDocFromCache> {
  if (!DEMO) return realGetDocFromCache(...args);
  return viaEngine((e) => e.getDoc(args[0] as DocumentReference)) as ReturnType<typeof realGetDocFromCache>;
}

export function getDocs(...args: Parameters<typeof realGetDocs>): ReturnType<typeof realGetDocs> {
  if (!DEMO) return realGetDocs(...args);
  return viaEngine((e) => e.getDocs(args[0] as Query)) as ReturnType<typeof realGetDocs>;
}

export function getDocsFromServer(...args: Parameters<typeof realGetDocsFromServer>): ReturnType<typeof realGetDocsFromServer> {
  if (!DEMO) return realGetDocsFromServer(...args);
  return viaEngine((e) => e.getDocs(args[0] as Query)) as ReturnType<typeof realGetDocsFromServer>;
}

export function getDocsFromCache(...args: Parameters<typeof realGetDocsFromCache>): ReturnType<typeof realGetDocsFromCache> {
  if (!DEMO) return realGetDocsFromCache(...args);
  return viaEngine((e) => e.getDocs(args[0] as Query)) as ReturnType<typeof realGetDocsFromCache>;
}

export function onSnapshot(...args: unknown[]): Unsubscribe {
  if (!DEMO) return (realOnSnapshot as (...a: unknown[]) => Unsubscribe)(...args);
  const [target, ...rest] = args as [DocumentReference | Query, ...unknown[]];
  if (engine) return engine.onSnapshot(target, rest);
  let unsubscribe: Unsubscribe | null = null;
  let cancelled = false;
  void engineReady.then(() => {
    if (!cancelled && engine) unsubscribe = engine.onSnapshot(target, rest);
  });
  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
}

// ── Writes ───────────────────────────────────────────────────────────────────

export function setDoc(...args: unknown[]): Promise<void> {
  if (!DEMO) return (realSetDoc as (...a: unknown[]) => Promise<void>)(...args);
  return viaEngine((e) => e.setDoc(args[0] as DocumentReference, args[1], args[2]));
}

export function updateDoc(...args: unknown[]): Promise<void> {
  if (!DEMO) return (realUpdateDoc as (...a: unknown[]) => Promise<void>)(...args);
  const [ref, ...rest] = args;
  return viaEngine((e) => e.updateDoc(ref as DocumentReference, rest));
}

export function addDoc(...args: Parameters<typeof realAddDoc>): ReturnType<typeof realAddDoc> {
  if (!DEMO) return realAddDoc(...args);
  return viaEngine((e) => e.addDoc(args[0], args[1])) as ReturnType<typeof realAddDoc>;
}

export function deleteDoc(...args: Parameters<typeof realDeleteDoc>): ReturnType<typeof realDeleteDoc> {
  if (!DEMO) return realDeleteDoc(...args);
  return viaEngine((e) => e.deleteDoc(args[0] as DocumentReference));
}

export function writeBatch(...args: Parameters<typeof realWriteBatch>): ReturnType<typeof realWriteBatch> {
  if (!DEMO) return realWriteBatch(...args);
  if (engine) return engine.writeBatch(args[0]) as ReturnType<typeof realWriteBatch>;
  // Before the engine arrives (never in practice: the app draws only after it), queue the batch's writes.
  const queued: Array<(batch: ReturnType<typeof realWriteBatch>) => void> = [];
  const later = {
    set: (...a: unknown[]) => (queued.push((b) => (b.set as (...x: unknown[]) => unknown)(...a)), later),
    update: (...a: unknown[]) => (queued.push((b) => (b.update as (...x: unknown[]) => unknown)(...a)), later),
    delete: (...a: unknown[]) => (queued.push((b) => (b.delete as (...x: unknown[]) => unknown)(...a)), later),
    commit: () =>
      engineReady.then(() => {
        const batch = (engine as DemoFirestoreEngine).writeBatch(args[0]) as ReturnType<typeof realWriteBatch>;
        queued.forEach((apply) => apply(batch));
        return batch.commit();
      }),
  };
  return later as unknown as ReturnType<typeof realWriteBatch>;
}

export function runTransaction(...args: Parameters<typeof realRunTransaction>): ReturnType<typeof realRunTransaction> {
  if (!DEMO) return realRunTransaction(...args);
  const [db, update, options] = args;
  return viaEngine((e) =>
    e.runTransaction(db, update as (transaction: unknown) => Promise<unknown>, options),
  ) as ReturnType<typeof realRunTransaction>;
}

// ── Sentinels ────────────────────────────────────────────────────────────────

export function serverTimestamp(): ReturnType<typeof realServerTimestamp> {
  const value = realServerTimestamp();
  if (DEMO) sentinels.set(value, { kind: 'serverTimestamp' });
  return value;
}

export function deleteField(): ReturnType<typeof realDeleteField> {
  const value = realDeleteField();
  if (DEMO) sentinels.set(value, { kind: 'deleteField' });
  return value;
}

export function increment(n: number): ReturnType<typeof realIncrement> {
  const value = realIncrement(n);
  if (DEMO) sentinels.set(value, { kind: 'increment', operand: n });
  return value;
}

export function arrayUnion(...elements: unknown[]): ReturnType<typeof realArrayUnion> {
  const value = realArrayUnion(...elements);
  if (DEMO) sentinels.set(value, { kind: 'arrayUnion', elements });
  return value;
}

export function arrayRemove(...elements: unknown[]): ReturnType<typeof realArrayRemove> {
  const value = realArrayRemove(...elements);
  if (DEMO) sentinels.set(value, { kind: 'arrayRemove', elements });
  return value;
}
